/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { IHoliday } from "../../interfaces/HolidayInterface";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getHolidays, postHoliday, patchHoliday, deleteHoliday } from "../../api/holiday";

export const holidaysQueryKey = "holidays";

// use holidays
export const useHolidays = (params?: {
  fiscalYear?: string | number;
}): UseQueryResult<Array<IHoliday>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [holidaysQueryKey, resourceCentre?.id, params?.fiscalYear],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getHolidays({
        resourceCentreId: Number(resourceCentre.id),
        ...params
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id
  });
};

// use post holiday
export const usePostHoliday = (): UseMutationResult<
  ApiResponse<IHoliday>,
  Error,
  Partial<IHoliday>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<IHoliday>) => postHoliday(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [holidaysQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Holiday created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create holiday",
          variant: "error"
        })
      );
    }
  });
};

// use patch holiday
export const usePatchHoliday = (): UseMutationResult<
  ApiResponse<IHoliday>,
  Error,
  { id: number | string; data: Partial<IHoliday> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchHoliday({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [holidaysQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Holiday updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update holiday",
          variant: "error"
        })
      );
    }
  });
};

// use delete holiday
export const useDeleteHoliday = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteHoliday(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [holidaysQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Holiday deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete holiday",
          variant: "error"
        })
      );
    }
  });
};
